import { format, parseISO } from "date-fns";
import { enUS, es } from "date-fns/locale";
import { ORDER_TYPES } from "../types/enums";
import {
  BIDDING_LABELS,
  BIDDING_TYPES,
  DELIVERY_STATES,
  PILL_LABELS,
} from "./enums";
import {
  OrderResponse,
  OrderState,
  DeliveryInterface,
  DeliveryState,
  FileType,
} from "../types";
import i18next from "i18next";

// Always renders the ISO currency code in front (e.g. "USD 1,000.00", "ARS 1,000.00") — never a
// symbol like "$", which `Intl` would otherwise use for USD but not ARS. `currencyDisplay: "code"`
// is the single knob that normalizes this across the app; every currency display goes through here.
export const currencyFormatter = (number?: number, coin?: string) => {
  if (!number) return;
  const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: coin ? coin : "ARS",
    currencyDisplay: "code",
  }).format(number);

  return currency;
};

export const cutIdOnFirstHyphen = (id?: string) => {
  if (!id) return "";
  return id.split("-")[0];
};

export const taxIdFormatter = (taxId: string) => {
  if (!taxId) return;
  const trimmedText = taxId.trim();
  const cleanedText = trimmedText.replace(/[^a-zA-Z0-9]/g, "");
  return cleanedText;
};

export const addressFormatter = (address: string | undefined) => {
  if (!address) return address;
  const result = address?.split(",").slice(-2).join(",");
  return result;
};

export const numberFormatter = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  roundingIncrement: 5,
});

export const getNumberOfDays = (end: string) => {
  const date1 = new Date();
  const date2 = new Date(end);
  date2.setDate(date2.getDate() + 1);
  const oneDay = 1000 * 60 * 60 * 24;
  const diffInTime = date2.getTime() - date1.getTime();
  const diffInDays = Math.round(diffInTime / oneDay);
  return diffInDays;
};

export const updateObject = (oldObject: any, updatedProperties: any) => {
  return {
    ...oldObject,
    ...updatedProperties,
  };
};

export const calculateMinimumDecrement = (
  price?: number,
  percentage?: number
) => {
  if (!price || !percentage) return;
  const res = (price * percentage) / 100;
  return (price - res).toFixed(2);
};

export const capitalize = (sentence: string, first?: boolean) => {
  if (!sentence) return;
  const words = sentence.split(" ");
  if (first) {
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  return words.join(" ");
};

export const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(" ");
};

export const getMonth = (date: string) => {
  const period = new Intl.DateTimeFormat("es-AR", {
    month: "long",
  }).format(new Date(date));
  return capitalize(period);
};

const BIDDING_TYPE_LABEL_KEYS: Partial<Record<string, string>> = {
  [BIDDING_TYPES.NORMAL]: "tenderTypes.normal",
  [BIDDING_TYPES.INVERSE]: "tenderTypes.inverse",
  [BIDDING_TYPES.JOINT]: "tenderTypes.joint",
  [BIDDING_TYPES.INVERSE_JOINT]: "tenderTypes.inverseJoint",
};

/**
 * The bidding type's label in the current language. Translated on each call,
 * not read from `BIDDING_TYPES_LABELS` (translated once at import, so it kept
 * the language the app booted in).
 */
export const labelType = (type: ORDER_TYPES) => {
  const key = BIDDING_TYPE_LABEL_KEYS[type];
  return key ? i18next.t(key) : undefined;
};

export const formatDate = (date: string) => {
  const language = localStorage.getItem("i18nextLng");
  const isEnglish =
    language === "en" ||
    language === "en-US" ||
    language === "en-GB" ||
    language === "en-AU" ||
    language === "en-CA" ||
    language === "en-IN" ||
    language === "en-NZ" ||
    language === "en-ZA";
  const langEs = !isEnglish;

  try {
    const parsedDate = parseISO(date);
    const formattedDate = format(parsedDate, "dd/MM/yyyy", {
      locale: langEs ? es : enUS,
    });
    return formattedDate.replace(
      /(\s[a-z])/g,
      (match) => match.toUpperCase() // Capitalizes the month
    );
  } catch (err) {
    console.error(err);
    return "";
  }
};

/**
 * Serialize a calendar date (order/bidding/supply dates — no meaningful time) for
 * the API anchored at NOON UTC. Sending UTC midnight ("YYYY-MM-DDT00:00:00.000Z")
 * let a backend that truncates to a LocalDate in a negative-offset zone roll the
 * day back by one (a date picked as the 18th was stored as the 17th). Noon UTC is
 * safe: any timezone within ±12h of UTC still lands on the same calendar day.
 * Accepts a date-only string ("YYYY-MM-DD") or any Date-parseable value.
 */
export const toApiCalendarDate = (value: string | number | Date): string =>
  new Date(
    Date.UTC(
      new Date(value).getUTCFullYear(),
      new Date(value).getUTCMonth(),
      new Date(value).getUTCDate(),
      12
    )
  ).toISOString();

export const formatDateTime = (dateString?: string) => {
  if (!dateString) return "";
  try {
    const parsedDate = parseISO(dateString);
    return format(parsedDate, "dd/MM/yyyy HH:mm");
  } catch (err) {
    console.error(err);
    return dateString;
  }
};

interface ResolverOptions {
  selected?: boolean;
  isSeller?: boolean;
}

export const pillResolver = (
  order: OrderResponse,
  options: ResolverOptions = {}
): string => {
  const { selected = false, isSeller = false } = options;

  if (!order?.state) return ""; // Guard clause for invalid or missing order

  switch (order.state) {
    case OrderState.SUCCEED:
      if (isSeller) {
        return selected
          ? PILL_LABELS.OFFER_SELECTED
          : PILL_LABELS.OFFER_NOT_SELECTED;
      }
      return PILL_LABELS.OFFER_SELECTED;

    case OrderState.PENDING:
      return BIDDING_LABELS.SCHEDULED;

    case OrderState.CONSULTATION:
      return BIDDING_LABELS.CONSULTATION;

    case OrderState.OPEN:
      return BIDDING_LABELS.IN_PROGRESS;

    case OrderState.CLOSED:
      return PILL_LABELS.OFFER_PENDING_OF_SELECTION;

    case OrderState.CANCELLED:
      return PILL_LABELS.BIDDING_CANCELED;

    case OrderState.DELIVERED:
      return PILL_LABELS.DELIVERED;

    case OrderState.REJECTED:
      return PILL_LABELS.REJECTED;

    case OrderState.DRAFT:
      return PILL_LABELS.DRAFT;

    default:
      return ""; // Return empty if no matching state
  }
};

export const deliveryResolver = (delivery: DeliveryInterface): string => {
  switch (delivery.state) {
    case DeliveryState.DRAFT:
      return DELIVERY_STATES.DRAFT;
    case DeliveryState.SCHEDULED:
      return DELIVERY_STATES.SCHEDULED;
    case DeliveryState.REJECTED:
      return DELIVERY_STATES.REJECTED;
    case DeliveryState.ACCEPTED:
      return DELIVERY_STATES.ACCEPTED;
    case DeliveryState.IN_PROGRESS:
      return DELIVERY_STATES.IN_PROGRESS;
    case DeliveryState.CANCELLED:
      return DELIVERY_STATES.CANCELLED;
    case DeliveryState.DELIVERED:
      return DELIVERY_STATES.DELIVERED;
    case DeliveryState.COMPLETED:
      return DELIVERY_STATES.COMPLETED;
    default:
      return ""; // Default to an empty string for unknown states
  }
};

export const fileTypeOptions = [
  {
    id: 1,
    value: FileType.ATTACHMENT,
    label: i18next.t("tenders.form.additionalDocument"),
  },
  {
    id: 2,
    value: FileType.CERTIFICATION,
    label: i18next.t("tenders.form.certificate"),
  },
  {
    id: 3,
    value: FileType.SHEET,
    label: i18next.t("tenders.form.plication"),
  },
  {
    id: 4,
    value: FileType.BRIEF,
    label: i18next.t("tenders.form.brief"),
  },
  {
    id: 5,
    value: FileType.SERVICE_PLACE,
    label: i18next.t("tenders.form.servicePlace"),
  },
];

// `calculateProfileCompletion` moved to
// `components/CompanyProfile/companyProfileCompletion.ts` (Task 7, Profile/Company flow) — it now
// lives alongside the wizard-step completion math it was duplicated against, and both of its
// consumers (`CompanyProfileForm.tsx`, `CompletionPercentage.tsx`) import it from there.

export const fileText = (type: FileType): string => {
  if (type === FileType.ATTACHMENT) {
    return i18next.t("tenders.form.additionalDocument");
  }
  if (type === FileType.SHEET) {
    return i18next.t("tenders.form.plication");
  }
  if (type === FileType.CERTIFICATION) {
    return i18next.t("tenders.form.certificate");
  }
  if (type === FileType.BRIEF) {
    return i18next.t("tenders.form.brief");
  }
  if (type === FileType.SERVICE_PLACE) {
    return i18next.t("tenders.form.servicePlace");
  }
  return i18next.t("cta.download");
};
